
import React, { useState } from 'react';
import { BookX, Play, Trash2, CheckCircle, AlertTriangle, BookOpen, Clock, Brain } from 'lucide-react';
import { Question, FSRSCollection } from '../data/types';
import { getTags } from '../utils';

interface MistakeReviewProps {
    wrongQuestions: number[];
    questions: Question[];
    removeFromMistakes: (id: number) => void;
    startQuizWithMistakes: (ids?: number[]) => void;
    fsrsData: FSRSCollection;
}

export const MistakeReviewView = ({ wrongQuestions, questions, removeFromMistakes, startQuizWithMistakes, fsrsData }: MistakeReviewProps) => {
    // Filter the full question list to find those in the mistake list
    const mistakeList = questions.filter(q => wrongQuestions.includes(q.id));
    
    // Sort logic:
    // 1. Due date passed (Urgent)
    // 2. No due date (New)
    // 3. Due date in future
    const now = new Date();
    
    const getDueDate = (id: number) => fsrsData[id] ? new Date(fsrsData[id].due) : null;
    
    const sortedList = [...mistakeList].sort((a, b) => {
        const dateA = getDueDate(a.id);
        const dateB = getDueDate(b.id);
        
        // Items without date (new) come after items with past due date but before future items?
        // Actually, FSRS creates new items immediately. If no date, treat as Due Now (very old logic) or New.
        // Let's treat "No Date" as "Due Now".
        const timeA = dateA ? dateA.getTime() : 0;
        const timeB = dateB ? dateB.getTime() : 0;
        
        return timeA - timeB;
    });

    const dueQuestions = sortedList.filter(q => {
        const date = getDueDate(q.id);
        return !date || date <= now;
    });

    const formatDueDate = (dateStr: string | Date) => {
        const date = new Date(dateStr);
        const diff = date.getTime() - now.getTime();
        const diffDays = Math.ceil(diff / (1000 * 60 * 60 * 24));
        
        if (diff <= 0) return <span className="text-red-600 font-bold">已到期</span>;
        if (diffDays === 1) return <span className="text-orange-500 font-bold">明天</span>;
        return <span className="text-gray-500">{diffDays} 天後</span>;
    };

    if (mistakeList.length === 0) {
        return (
            <div className="flex-1 bg-gray-50 flex flex-col items-center justify-center p-8 text-center animate-fade-in">
                <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mb-6">
                    <CheckCircle className="w-12 h-12 text-green-500" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">太棒了！目前沒有錯題</h2>
                <p className="text-gray-500 max-w-md">
                    錯題本是空的，代表你已經掌握了目前的練習內容，或者還沒開始進行測驗。繼續保持！
                </p>
            </div>
        );
    }

    return (
        <div className="flex-1 bg-gray-50 overflow-hidden flex flex-col">
            {/* Header / Stats */}
            <div className="bg-white border-b border-gray-200 px-6 py-6 shadow-sm shrink-0">
                <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
                    <div>
                        <div className="flex items-center text-red-600 mb-1">
                            <BookX className="w-6 h-6 mr-2" />
                            <h2 className="text-2xl font-bold font-serif">錯題智慧複習</h2>
                        </div>
                        <p className="text-gray-500 text-sm">
                            總累積：<span className="font-bold text-gray-800">{mistakeList.length}</span> 題
                            <span className="mx-2">|</span>
                            <span className="text-red-600 font-bold">今日需複習：{dueQuestions.length} 題</span>
                        </p>
                    </div>
                    <div className="flex space-x-3">
                        {dueQuestions.length > 0 && (
                            <button
                                onClick={() => startQuizWithMistakes(dueQuestions.map(q => q.id))}
                                className="flex items-center px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-full font-bold shadow-lg transition-transform transform hover:scale-105"
                            >
                                <Brain className="w-5 h-5 mr-2" />
                                智慧複習 ({dueQuestions.length})
                            </button>
                        )}
                        <button
                            onClick={() => startQuizWithMistakes()}
                            className="flex items-center px-6 py-3 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-full font-bold shadow-sm transition-colors"
                        >
                            <Play className="w-5 h-5 mr-2" />
                            全部複習
                        </button>
                    </div>
                </div>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto p-4 md:p-8">
                <div className="max-w-6xl mx-auto space-y-4">
                    {sortedList.map((q) => {
                        const fsrs = fsrsData[q.id];
                        const isDue = !fsrs || new Date(fsrs.due) <= now;
                        
                        return (
                            <div key={q.id} className={`bg-white p-5 rounded-xl border ${isDue ? 'border-red-200 ring-1 ring-red-50' : 'border-gray-200'} shadow-sm hover:shadow-md transition-shadow group flex flex-col md:flex-row gap-4`}>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        {isDue ? (
                                            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-bold flex items-center">
                                                <Clock className="w-3 h-3 mr-1" /> Due
                                            </span>
                                        ) : (
                                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-bold flex items-center">
                                                <Clock className="w-3 h-3 mr-1" /> Future
                                            </span>
                                        )}
                                        <span className="text-xs font-mono text-gray-400">ID: {q.id}</span>
                                        {getTags(q.category).map(tag => (
                                            <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded border border-gray-200">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-800 mb-3 line-clamp-2 md:line-clamp-none">
                                        {q.text}
                                    </h3>
                                    
                                    <div className="hidden group-hover:block bg-gray-50 p-3 rounded-lg border border-gray-100 mt-2 animate-fade-in">
                                        <div className="flex items-start text-sm text-gray-700">
                                            <BookOpen className="w-4 h-4 text-gray-400 mr-2 mt-0.5 shrink-0" />
                                            <div className="flex-1">
                                                <span className="font-bold text-gray-500 mb-1 block">正確解答：</span>
                                                {q.explanation}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex md:flex-col justify-between md:justify-center items-end gap-2 border-t md:border-t-0 border-gray-100 pt-3 md:pt-0 min-w-[120px]">
                                    <div className="text-xs text-gray-500 text-right w-full mb-1">
                                        <div className="flex justify-between md:justify-end items-center md:mb-1">
                                            <span className="md:hidden mr-2">下次複習:</span>
                                            {fsrs ? formatDueDate(fsrs.due) : <span className="text-red-500 font-bold">立即</span>}
                                        </div>
                                        {fsrs && (
                                            <div className="text-[10px] text-gray-400">
                                                穩定度: {fsrs.stability.toFixed(1)} | 難度: {fsrs.difficulty.toFixed(1)}
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => removeFromMistakes(q.id)}
                                        className="flex items-center px-3 py-1.5 text-sm text-green-600 hover:bg-green-50 rounded-lg transition-colors border border-transparent hover:border-green-200 w-full justify-end md:justify-center"
                                        title="我已經完全學會了，移出錯題本"
                                    >
                                        <CheckCircle className="w-4 h-4 mr-1" />
                                        已掌握
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
